import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { FolderOpen, Upload, User, Shield, ArrowRight, FileText, Clock, ClipboardList, Download, MessageSquare, Briefcase, Bell, ScanLine, CalendarClock, Wallet, FileSignature, Network, Inbox } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { FreigabenCard } from '../components/FreigabenCard';
import { WelcomeTour } from '../components/WelcomeTour';
import { WelcomeGreeting } from '../components/WelcomeGreeting';
import { MandantKontext } from '../components/MandantKontext';
import PortalStart from './PortalStart';

const PORTAL_TOUR = [
  { key: 'cases', target: 'portal-dock-akten' },
  { key: 'chat', target: 'portal-dock-chat' },
  { key: 'documents', target: 'portal-dock-dokumente' },
];
const PORTAL_TIPPS = [
  { key: 'tipp_raten', target: 'portal-dock-zahlungen' },
  { key: 'tipp_scan', target: 'portal-dock-dokumente' },
];

const TOUR_STEPS = [
  { key: 'cases', target: 'hero-cases-stat' },
  { key: 'chat', target: 'dashboard-card--meine-akten' },
  { key: 'documents', target: 'dashboard-card--dokumente' },
];
const TIPP_STEPS = [
  { key: 'tipp_raten', target: 'dashboard-card--meine-raten' },
  { key: 'tipp_scan', target: 'dashboard-card--dokumente-scannen' },
];
const TIPP_NACH_MS = 3 * 24 * 60 * 60 * 1000;
const TEAM_TOUR_STEPS = [
  { key: 'team_portal', target: 'dashboard-card--team-portal' },
  { key: 'team_register', target: 'dashboard-card--admin-mandanten' },
  { key: 'team_offen', target: 'dashboard-card--admin-mandanten-offen' },
];

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tourManuell, setTourManuell] = useState(params.get('tour') === '1');
  useEffect(() => { if (params.get('tour')) setParams({}, { replace: true }); }, [params, setParams]);
  const istTeam = !!user && user.role !== 'user';
  const tourAktiv = !!user && (tourManuell || user.tour_offen);
  const tippsAktiv = !tourAktiv && user?.role === 'user' && !user.tipps_erledigt && !!user.tour_erledigt_am
    && Date.now() - new Date(user.tour_erledigt_am).getTime() >= TIPP_NACH_MS;
  const tippsBeenden = () => {
    setUser((u) => ({ ...u, tipps_erledigt: true }));
    api.post('/auth/tipps-erledigt').catch(() => {});
  };
  const [begruesst, setBegruesst] = useState(false);
  const zeigeBegruessung = tourAktiv && !istTeam && !tourManuell && !begruesst;
  const tourBeenden = (abgeschlossen) => {
    setTourManuell(false);
    if (user?.tour_offen) {
      setUser((u) => ({ ...u, tour_offen: false }));
      api.post('/auth/tour-erledigt', { abgeschlossen }).catch(() => {});
    }
  };
  const [stats, setStats] = useState({ documents: 0, lastUpload: null });
  const [recent, setRecent] = useState([]);
  const [casesSummary, setCasesSummary] = useState({ total: 0, unread: 0 });
  const [termine, setTermine] = useState([]);
  const [akonto, setAkonto] = useState([]);
  const [esign, setEsign] = useState([]);

  useEffect(() => {
    api.get('/termine/meine').then((r) => setTermine(r.data.items || [])).catch(() => setTermine([]));
    api.get('/akonto/meine').then((r) => setAkonto(r.data.items || [])).catch(() => setAkonto([]));
    api.get('/esign/meine/liste').then((r) => setEsign(r.data.items || [])).catch(() => setEsign([]));
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/documents');
        setStats({ documents: data.length, lastUpload: data[0]?.created_at });
        setRecent(data.slice(0, 3));
      } catch (e) {
        console.warn('[Dashboard] Dokumente konnten nicht geladen werden:', e?.response?.status || e?.message);
      }
      try {
        const { data: cs } = await api.get('/cases');
        const items = cs?.items || [];
        const unread = items.reduce((sum, c) => sum + (c.unread || 0), 0);
        setCasesSummary({ total: items.length, unread });
      } catch (e) {
        console.warn('[Dashboard] Akten konnten nicht geladen werden:', e?.response?.status || e?.message);
      }
    })();
  }, []);

  const initials = (user?.name || user?.email || 'M')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Reduzierte Navigation für Mandanten – nur die 3 Kern-Aktionen
  const cards = [
    {
      to: '/meine-akten',
      icon: MessageSquare,
      title: 'dashboard.cards.cases_chat.title',
      desc: 'dashboard.cards.cases_chat.desc',
      color: '#b8893d',
      badge: casesSummary.unread > 0 ? t('dashboard.cards.cases_chat.badge', { count: casesSummary.unread }) : null,
    },
    { to: '/dokumente', icon: FolderOpen, title: 'dashboard.cards.documents.title', desc: 'dashboard.cards.documents.desc', color: '#1B3A5C' },
    { to: '/dokumente/scannen', icon: ScanLine, title: 'dashboard.cards.scan.title', desc: 'dashboard.cards.scan.desc', color: '#b8893d' },
    { to: '/meine-raten', icon: CalendarClock, title: 'dashboard.cards.raten.title', desc: 'dashboard.cards.raten.desc', color: '#b8893d' },
    { to: '/akontozahlung', icon: Wallet, title: 'dashboard.cards.akonto.title', desc: 'dashboard.cards.akonto.desc', color: '#1B3A5C' },
    { to: '/profil', icon: User, title: 'dashboard.cards.profile.title', desc: 'dashboard.cards.profile.desc', color: '#34536b' },
  ];
  if (['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user?.role)) {
    cards.push({ to: '/admin', icon: Shield, title: 'dashboard.cards.admin.title', desc: 'dashboard.cards.admin.desc', color: '#b8893d' });
    cards.push({ to: '/kanzlei/eingang', icon: Briefcase, title: 'dashboard.cards.team.title', desc: 'dashboard.cards.team.desc', color: '#1B3A5C' });
    cards.push({ to: '/kanzlei/mandanten', icon: Network, title: 'dashboard.cards.register.title', desc: 'dashboard.cards.register.desc', color: '#34536b' });
    cards.push({ to: '/kanzlei/mandanten/offen', icon: Inbox, title: 'dashboard.cards.offen.title', desc: 'dashboard.cards.offen.desc', color: '#b8893d' });
  }

  if (user?.role === 'user') return (
    <>
      <PortalStart />
      {zeigeBegruessung && <WelcomeGreeting name={user?.name?.split(' ')[0] || ''} onContinue={() => setBegruesst(true)} />}
      {tourAktiv && !zeigeBegruessung && <WelcomeTour steps={PORTAL_TOUR} onFinish={tourBeenden} />}
      {tippsAktiv && <WelcomeTour steps={PORTAL_TIPPS} onFinish={tippsBeenden} />}
    </>
  );
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[150px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        {user?.must_change_password && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm" data-testid="must-change-password-banner">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center justify-between gap-3">
              <span>{t('dashboard.must_change_password')}</span>
              <Link to="/profil" className="text-xs tracking-wider px-4 py-2 bg-[#1B3A5C] text-white rounded-sm" data-testid="must-change-password-link">{t('dashboard.must_change_password_cta')}</Link>
            </div>
          </div>
        )}
        <section className="bg-[#1B3A5C] text-white py-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#b8893d] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-[#b8893d] object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#b8893d] flex items-center justify-center text-white font-serif text-2xl">
                    {initials}
                  </div>
                )}
                <div>
                  <p className="text-[#e0b56a] text-xs tracking-[0.3em]">{t('dashboard.hero.subtitle')}</p>
                  <h1 className="font-serif text-3xl md:text-4xl mt-1">
                    {t('dashboard.hero.welcome', { name: user?.name?.split(' ')[0] || t('dashboard.hero.fallback_name') })}
                  </h1>
                  {user?.role === 'user' && <div className="mt-3"><MandantKontext /></div>}
                  <p className="text-white/70 text-sm mt-1">{user?.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 w-full md:w-auto">
                <Link
                  to="/meine-akten"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg p-4 text-left transition-colors relative group min-w-[150px]"
                  data-testid="hero-cases-stat"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <MessageSquare className="w-5 h-5 text-[#e0b56a]" />
                    {casesSummary.unread > 0 && (
                      <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 rounded-full min-w-[20px] text-center animate-pulse" data-testid="hero-unread-badge">
                        {casesSummary.unread}
                      </span>
                    )}
                  </div>
                  <p className="font-serif text-2xl leading-none">{casesSummary.total}</p>
                  <p className="text-[10px] text-white/70 tracking-[0.15em] mt-1">{t('dashboard.hero.stats.open_cases')}</p>
                  <ArrowRight className="w-3 h-3 absolute bottom-3 right-3 text-white/40 group-hover:text-white/80 group-hover:translate-x-0.5 transition-all" />
                </Link>
                <Link
                  to="/meine-akten"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg p-4 text-left transition-colors group min-w-[150px]"
                >
                  <Bell className="w-5 h-5 text-[#e0b56a] mb-1.5" />
                  <p className="font-serif text-2xl leading-none">{casesSummary.unread}</p>
                  <p className="text-[10px] text-white/70 tracking-[0.15em] mt-1">{t('dashboard.hero.stats.new_messages')}</p>
                </Link>
                <Link
                  to="/dokumente"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg p-4 text-left transition-colors group min-w-[150px]"
                >
                  <FileText className="w-5 h-5 text-[#e0b56a] mb-1.5" />
                  <p className="font-serif text-2xl leading-none">{stats.documents}</p>
                  <p className="text-[10px] text-white/70 tracking-[0.15em] mt-1">{t('dashboard.hero.stats.documents')}</p>
                </Link>
                <Link
                  to="/dokumente?upload=1"
                  className="bg-[#b8893d] hover:bg-[#a07730] rounded-lg p-4 text-left transition-colors group min-w-[150px] flex flex-col justify-center"
                  data-testid="hero-upload-cta"
                >
                  <Upload className="w-5 h-5 text-white mb-1.5" />
                  <p className="text-white text-sm font-semibold leading-tight whitespace-pre-line">{t('dashboard.hero.upload_cta.label')}</p>
                  <p className="text-[10px] text-white/70 tracking-[0.15em] mt-1">{t('dashboard.hero.upload_cta.badge')}</p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FreigabenCard />

        {termine.length > 0 && (
          <section className="pt-10" data-testid="dashboard-termine">
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
              <h2 className="font-serif text-2xl text-[#1B3A5C] mb-4">{t('dashboard.termine.heading')}</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {termine.map((tm) => (
                  <li key={tm.termin_id} className="bg-white border-l-4 border-[#b8893d] rounded-sm shadow-sm p-5" data-testid={`dashboard-termin-${tm.termin_id}`}>
                    <div className="text-[11px] tracking-[0.28em] text-[#b8893d] uppercase">{tm.art_label}</div>
                    <div className="font-medium text-[#1B3A5C] mt-1">{tm.anzeige}</div>
                    {tm.betreff && <div className="text-sm text-[#34536b] mt-1">{tm.betreff}</div>}
                    <div className="text-xs text-[#7a8a99] mt-2">{tm.ort}</div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
        {esign.length > 0 && (
          <section className="pt-10" data-testid="dashboard-esign">
            <div className="flex items-center gap-3 text-[#b8893d] text-xs tracking-[0.3em] mb-4"><span className="w-12 h-px bg-[#b8893d]"></span>{t('esign.kicker').toUpperCase()}</div>
            <ul className="space-y-3">
              {esign.map((v) => (
                <li key={v.vorgang_id} className="bg-white border border-gray-100 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`dashboard-esign-${v.vorgang_id}`}>
                  <div className="flex items-start gap-3 min-w-0">
                    <FileSignature className="w-5 h-5 text-[#b8893d] shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1B3A5C]">{v.dokumente.map((d) => t(`esign.dok.${d}`, d)).join(' + ')}</div>
                      <div className="text-xs text-[#7a8a99] mt-0.5">{v.mandat?.gegenstand}{v.mandat?.aktenzeichen ? ` · Az. ${v.mandat.aktenzeichen}` : ''} · {new Date(v.created_at).toLocaleDateString('de-DE')}</div>
                    </div>
                  </div>
                  <Link to={v.link} className={`text-[10px] tracking-wider uppercase px-3 py-2 rounded-sm ${['signiert', 'abgeschlossen'].includes(v.status) ? 'bg-emerald-50 text-emerald-700' : 'bg-[#1B3A5C] text-white hover:bg-[#15304d]'}`} data-testid={`dashboard-esign-link-${v.vorgang_id}`}>{['signiert', 'abgeschlossen'].includes(v.status) ? `${t('esign.signiert_pdf')} ✓` : t('esign.jetzt_signieren')}</Link>
                </li>
              ))}
            </ul>
          </section>
        )}
        {akonto.length > 0 && (
          <section className="pt-10" data-testid="dashboard-akonto">
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
              <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
                <h2 className="font-serif text-2xl text-[#1B3A5C]">{t('dashboard.akonto.heading')}</h2>
                <Link to="/akontozahlung" className="text-xs tracking-wider text-[#b8893d] hover:underline" data-testid="dashboard-akonto-neu">{t('dashboard.akonto.neu')} →</Link>
              </div>
              <div className="bg-white rounded-sm shadow-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="dashboard-akonto-table">
                  <thead><tr className="text-[10px] tracking-wider text-[#7a8a99] text-left bg-[#f5f8fa]"><th className="px-4 py-2 font-medium">{t('dashboard.akonto.datum')}</th><th className="px-4 py-2 font-medium">{t('dashboard.akonto.referenz')}</th><th className="px-4 py-2 font-medium">{t('dashboard.akonto.zweck')}</th><th className="px-4 py-2 font-medium text-right">{t('dashboard.akonto.betrag')}</th><th className="px-4 py-2 font-medium">{t('dashboard.akonto.status')}</th></tr></thead>
                  <tbody>
                    {akonto.map((z) => (
                      <tr key={z.akonto_id} className="border-t border-gray-100" data-testid={`dashboard-akonto-${z.akonto_id}`}>
                        <td className="px-4 py-3 text-[#34536b] whitespace-nowrap">{new Date(z.paid_at || z.created_at).toLocaleDateString('de-DE')}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#1B3A5C]">{[z.mandantennummer && `M ${z.mandantennummer}`, z.aktenzeichen && `AZ ${z.aktenzeichen}`].filter(Boolean).join(' · ') || (z.referenz ? `${z.referenz_typ === 'mandantennummer' ? 'M' : 'AZ'} ${z.referenz}` : '—')}</td>
                        <td className="px-4 py-3 text-xs text-[#34536b] max-w-[240px] truncate">{z.verwendungszweck || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-[#1B3A5C] whitespace-nowrap">{z.betrag.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                        <td className="px-4 py-3"><span className={`text-[10px] tracking-wider px-2 py-1 rounded-sm ${z.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : ['pending', 'angefordert'].includes(z.status) ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>{t(`dashboard.akonto.status_${z.status}`, z.status_label)}</span>
                          {z.pay_url && <Link to={z.pay_url} className="ml-2 inline-block text-[10px] tracking-wider uppercase px-2 py-1 bg-[#1B3A5C] text-white rounded-sm hover:bg-[#15304d]" data-testid={`dashboard-akonto-pay-${z.akonto_id}`}>{t('akonto.angebot.jetzt_zahlen')}</Link>}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="border-t border-[#1B3A5C]/20"><td className="px-4 py-3 font-semibold text-[#1B3A5C]" colSpan={3}>{t('dashboard.akonto.summe')}</td><td className="px-4 py-3 text-right font-semibold text-[#1B3A5C]" data-testid="dashboard-akonto-summe">{akonto.filter((z) => z.status === 'paid').reduce((s, z) => s + z.betrag, 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td><td /></tr></tfoot>
                </table>
              </div>
            </div>
          </section>
        )}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-6 lg:px-10">
            <h2 className="font-serif text-2xl text-[#1B3A5C] mb-6">{t('dashboard.actions.heading')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {cards.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`dashboard-card-${c.to.replace(/[^a-z0-9]/gi, '-')}`}
                  className="group bg-white p-6 rounded-sm border-l-4 hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 relative"
                  style={{ borderColor: c.color }}
                >
                  {c.badge && (
                    <span className="absolute top-4 right-4 bg-red-500 text-white text-[10px] tracking-wider px-2 py-0.5 rounded-sm animate-pulse" data-testid="cases-unread-badge">
                      {c.badge}
                    </span>
                  )}
                  <div
                    className="w-12 h-12 rounded-sm flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: c.color }}
                  >
                    <c.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-[#1B3A5C] font-semibold tracking-wide mb-1">{t(c.title)}</h3>
                  <p className="text-[#34536b] text-sm leading-relaxed">{t(c.desc)}</p>
                  <div className="flex items-center gap-1 text-[#b8893d] text-xs tracking-wider mt-4 group-hover:gap-2 transition-all">
                    {t('dashboard.actions.open_link')} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>

            {recent.length > 0 && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl text-[#1B3A5C] mb-6 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#b8893d]" />
                  {t('dashboard.recent.heading')}
                </h2>
                <div className="bg-white rounded-sm shadow-sm">
                  {recent.map((d, i) => (
                    <div key={d.doc_id} className={`flex items-center gap-4 p-4 ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                      <div className="w-10 h-10 bg-[#f5f8fa] rounded-sm flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#b8893d]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1B3A5C] font-medium text-sm truncate">{d.filename}</p>
                        <p className="text-[#34536b] text-xs">
                          {t('dashboard.recent.file_meta', { size: (d.size / 1024).toFixed(1), date: new Date(d.created_at).toLocaleString('de-DE') })}
                        </p>
                      </div>
                      <span className="text-[10px] bg-[#b8893d]/10 text-[#b8893d] px-2 py-1 rounded-sm tracking-wider">
                        {d.uploaded_by === 'kanzlei' ? t('dashboard.recent.uploaded_by_firm') : t('dashboard.recent.uploaded_by_me')}
                      </span>
                    </div>
                  ))}
                  <Link
                    to="/dokumente"
                    className="flex items-center justify-center gap-1 py-3 border-t border-gray-100 text-[#b8893d] text-sm hover:bg-[#fafaf7] transition-colors"
                  >
                    {t('dashboard.recent.view_all')} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      {zeigeBegruessung && <WelcomeGreeting name={user?.name?.split(' ')[0] || ''} onContinue={() => setBegruesst(true)} />}
      {tourAktiv && !zeigeBegruessung && <WelcomeTour steps={istTeam ? TEAM_TOUR_STEPS : TOUR_STEPS} onFinish={tourBeenden} />}
      {tippsAktiv && <WelcomeTour steps={TIPP_STEPS} onFinish={tippsBeenden} />}
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default Dashboard;
